import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartKey } from '@/components/charts/legend';
import { useChartTheme } from '@/components/charts/theme';
import type { FishGroupTransferOperationType, LineageEdge } from '@/domain/fishGroups';

import { LINEAGE_EDGE_COLORS } from './lineageEdgeColors';

import styles from './LineageGraph.module.scss';

interface Props {
    edges: LineageEdge[];
}

// Display order is independent of how operations are inferred at the
// backend; the same order is used for the legend regardless of which
// types are present in a given graph.
const LEGEND_ORDER: readonly FishGroupTransferOperationType[] = [
    'split',
    'merge',
    'split_and_merge',
    'redistribution',
];

export default function LineageLegend(props: Props) {
    const { edges } = props;
    const { t } = useTranslation('fishGroups');
    const theme = useChartTheme();

    const presentTypes = useMemo(() => {
        const set = new Set<FishGroupTransferOperationType>();
        for (const edge of edges) {
            set.add(edge.transfer.operationType);
        }
        return LEGEND_ORDER.filter((type) => set.has(type));
    }, [edges]);

    const hasReverted = edges.some((edge) => edge.transfer.revertedByOperationId !== null);

    if (presentTypes.length === 0) return null;

    return (
        <ChartKey
            className={styles.legend}
            nowrapLabels={false}
            aria-label={t('detail.lineage.operationLegend.label')}
            entries={[
                ...presentTypes.map((type) => ({
                    id: type,
                    label: t(`detail.lineage.operationLegend.${type}`),
                    swatch: {
                        shape: 'line' as const,
                        color: theme.color(LINEAGE_EDGE_COLORS[type]),
                    },
                })),
                ...(hasReverted
                    ? [
                          {
                              id: 'reverted',
                              label: t('detail.lineage.operationLegend.reverted'),
                              swatch: {
                                  shape: 'dashed' as const,
                                  color: 'var(--color-text-secondary)',
                              },
                          },
                      ]
                    : []),
            ]}
        />
    );
}
